import { SalePurpose } from '@app/domain/Sale'
import * as d3 from 'd3'
import { AccountUser } from '@app/domain/AccountUser'
import { Area } from '@app/domain/Area'
import { Booking } from '@app/domain/Booking'
import { BookingCommunicationMessage } from '@app/domain/BookingCommunicationMessage'
import { BookingCommunicationService } from '@services/booking-communication.service'
import { BookingService } from '@services/booking.service'
import { activeBookingStatusTypes, BookingStatus, BookingStatusType } from '@app/domain/BookingStatus'
import {
    BookingViewModel,
    BookingViewModelDelegate,
} from '@app/features/bookings/components/booking-view-model'
import { Business } from '@app/domain/Business'
import { CancellationReason as ConfiguredCancellationReason } from '@app/domain/CancellationReason'
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core'
import {
    ConfirmUndoCancellationComponent,
} from '../confirm-undo-cancellation/confirm-undo-cancellation.component'
import { ContextService } from '@services/context.service'
import { Customer } from '../../../../domain/Customer'
import { DateTime } from 'luxon'
import { DiaryPreferencesService } from '@app/features/bookings/services/diary-preferences.service'
import { Dinero } from 'dinero.js'
import { Helper } from '@app/helpers/utilities/helper'
import {
    ModalActionType,
    ModalComponent,
} from '@app/features/shared/components/modal/modal.component'
import { NgbActiveModal, NgbActiveOffcanvas, NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { EMPTY, Observable, Subject, catchError, exhaustMap, filter, finalize, forkJoin, mergeMap, takeUntil, tap, map, startWith, of } from 'rxjs'
import { BillingService } from '@app/services/billing.service'
import { BillingDetails } from '@app/domain/BillingDetails'
import { BookingOrderEditComponent } from '@app/features/shared/components/booking-order-edit/booking-order-edit.component'
import { BookingOrderDocketEditComponent } from '@app/features/shared/components/booking-order-docket-edit/booking-order-docket-edit.component'
import { BookingDocket } from '@app/domain/BookingDocket'
import { Order } from '@app/domain/Order'
import { Organisation } from '@app/domain/Organisation'
import { PaymentsService } from '@services/payments.service'
import { ScheduleService } from '@services/schedule.service'
import { ToastBuilder } from '@app/domain/ToastBuilder'
import { ToastService } from '@services/toast.service'
import { ToastType } from '@app/domain/Toast'
import { Venue } from '@app/domain/Venue'
import { VenueSchedule } from '@app/domain/VenueSchedule'
import { v4 as uuidv4 } from 'uuid'
import { MenuLinkType } from '@app/domain/MenuLinkType'
import { MessageThread } from '@app/domain/MessageThread'
import { MessagesUnreadCountPublisher } from '@app/domain/MessagesUnreadCountPublisher'
import { RebookBookingComponent } from '../rebook-booking/rebook-booking.component'
import { FormControl, Validators } from '@angular/forms'
import { ServiceStatus } from '@app/domain/ServiceStatus'
import { chargeFailureMessage, isChargeFailure } from '@app/domain/PaymentDeclineMessage'

export type BookingChangedMetadata = {
    changeHasFinishedInteraction: boolean
}

type CancelBookingPaymentOptions = Partial<{
    mechanism: 'cancellationCharge' | 'depositRefund',
    coversToCharge: number | null,
    orderRefundLineIds: string[],
}>

@Component({
    selector: 'app-booking',
    templateUrl: './booking.component.html',
    styleUrls: ['./booking.component.sass'],
})
export class BookingComponent implements
    OnInit,
    OnDestroy,
    BookingViewModelDelegate {

    @Input() booking!: Booking
    @Input() organisation!: Organisation
    @Input() business!: Business
    @Input() venue!: Venue
    @Input() otherBookings!: Booking[]
    @Input() schedule!: VenueSchedule | null
    @Output() bookingBeingChanged = new EventEmitter<boolean>()
    @Output() bookingChanged = new EventEmitter<[Booking, Partial<BookingChangedMetadata>]>()
    viewModel!: BookingViewModel
    @Input() page: 'details' | 'customer' | 'previous-visits' | 'customer-feedback' | 'payments' | 'history' | 'order' | 'messages' = 'details'
    isEditingBooking = false
    messageThread: MessageThread | null = null
    messages: BookingCommunicationMessage[] = []
    isLoadingMessages = false
    isSendingMessage = false
    partySizePluralMapping = {
        '=0': 'No people',
        '=1': '1 person',
        other: '# people',
    }
    showRebookButton = false
    hasOrdersAddon$!: Observable<boolean>
    chargeFailureMessage: string | null = null
    bookingConfirmationDeliveryMethod = new FormControl<string | null>(null, [Validators.required])
    orderLinkDeliveryMethod = new FormControl<string | null>(null, [Validators.required])
    merchantCancellationReasonId = new FormControl<string | null>(null)
    selectedStatusOrderLineIds = new Set<string>()
    showStatusProductRefundItems = false
    statusRefundTime = new Date()
    statusActionTemplate!: TemplateRef<any>
    @ViewChild('statusActionModalBody', { static: true }) statusActionModalBody!: TemplateRef<any>
    private statusRefundModal: { component: ModalComponent, actionTitle: string, cancelTitle: string, refundDeposit: boolean } | null = null
    private area?: Area
    private onDestroy = new Subject<void>()

    constructor(
        private contextService: ContextService,
        protected diaryPreferencesService: DiaryPreferencesService,
        private bookingCommunicationService: BookingCommunicationService,
        private bookingService: BookingService,
        private paymentService: PaymentsService,
        private scheduleService: ScheduleService,
        private messagesUnreadCountPublisher: MessagesUnreadCountPublisher,
        private toastService: ToastService,
        @Optional() private modal: NgbActiveModal | null,
        @Optional() private activeOffcanvas: NgbActiveOffcanvas | null,
        private modalService: NgbModal,
        private offcanvasService: NgbOffcanvas,
        private billingService: BillingService,
    ) { }

    ngOnInit() {
        this.showRebookButton = this.contextService.getUser()?.isAdministrator() === true
        this.area = this.venue.areaBookingIsIn(this.booking) || undefined
        this.hasOrdersAddon$ = this.billingService.getCachedBillingDetails().pipe(
            map((billing: BillingDetails) => billing.hasOrdersAddon())
        )
        this.viewModel = new BookingViewModel(
            this.booking,
            this.venue,
            this
        )
        this.bookingChanged
            .pipe(
                takeUntil(this.onDestroy)
            )
            .subscribe(([booking, _]) => {
                this.booking = booking
                this.area = this.venue.areaBookingIsIn(this.booking) || undefined
                this.viewModel = new BookingViewModel(
                    this.booking,
                    this.venue,
                    this
                )
                if (this.page === 'messages') {
                    this.messageThread = this.threadFromBooking()
                }
            })
        this.bookingBeingChanged
            .pipe(
                takeUntil(this.onDestroy)
            )
            .subscribe(isBeingChanged => {
                this.isEditingBooking = isBeingChanged
            })
    }

    ngOnDestroy() {
        this.onDestroy.next()
        this.onDestroy.complete()
    }

    get shouldAskMerchantCancellationReason(): boolean {
        return this.venue.diaryPreferences.merchantRequiresCancellationReason !== null
    }

    get merchantCancellationReasonRequired(): boolean {
        return this.venue.diaryPreferences.merchantRequiresCancellationReason === true
    }

    get merchantCancellationReasons(): ConfiguredCancellationReason[] {
        return this.venue.cancellationReasonsSortedByOrderIndex
    }

    get showMerchantCancellationReasonValidation(): boolean {
        return this.merchantCancellationReasonId.invalid && this.merchantCancellationReasonId.touched
    }

    get canResendBookingConfirmation(): boolean {
        return this.booking.dateVoided === null && (!!this.booking.emailAddress || !!this.booking.phoneNumber)
    }

    get hasPrimaryBookingActions(): boolean {
        return this.viewModel.canRejectBooking ||
            this.viewModel.canRejectAndRefundBooking ||
            this.viewModel.canAcceptBooking ||
            this.viewModel.canSeatParty ||
            this.viewModel.canPartiallySeatParty ||
            this.viewModel.canSetPartyToWaiting ||
            this.viewModel.showMarkAsRunningLateButton ||
            this.viewModel.canUnseatParty ||
            this.viewModel.canFinishBooking ||
            this.viewModel.canReseatParty ||
            this.viewModel.canUndoCancellation ||
            this.viewModel.showCancelBookingButton ||
            this.viewModel.showCancelAndChargeButton ||
            this.viewModel.showCancelAndRefundDepositButton ||
            this.viewModel.showNoShowBookingButton ||
            this.viewModel.showNoShowAndChargeButton ||
            this.viewModel.showNoShowAndRefundDepositButton
    }

    get hasSecondaryBookingActions(): boolean {
        return this.canResendBookingConfirmation ||
            this.viewModel.canVoidBooking
    }

    customerInitials(customer: Customer): string {
        const initials = [customer.firstName, customer.lastName]
            .map(name => name?.trim().charAt(0) || '')
            .join('')
            .toUpperCase()

        return initials || '?'
    }

    private configureMerchantCancellationReasonControl(component: ModalComponent) {
        this.merchantCancellationReasonId.reset(null)
        this.merchantCancellationReasonId.clearValidators()
        if (this.merchantCancellationReasonRequired) {
            this.merchantCancellationReasonId.addValidators(Validators.required)
        }
        this.merchantCancellationReasonId.updateValueAndValidity()

        component.actionValidation = this.merchantCancellationReasonId.valueChanges
            .pipe(
                startWith(this.merchantCancellationReasonId.value),
                map(() => this.canSubmitMerchantCancellationReason())
            )
    }

    private canSubmitMerchantCancellationReason(): boolean {
        return !this.merchantCancellationReasonRequired || this.merchantCancellationReasonId.valid
    }

    private selectedMerchantCancellationReason(): ConfiguredCancellationReason | null {
        const cancellationReasonId = this.merchantCancellationReasonId.value
        if (!cancellationReasonId) {
            return null
        }
        return this.venue.cancellationReasonWithId(cancellationReasonId)
    }

    private cancelBookingFromModal(
        user: AccountUser,
        area: Area,
        paymentOptions: CancelBookingPaymentOptions
    ): Observable<Booking> {
        if (!this.canSubmitMerchantCancellationReason()) {
            this.merchantCancellationReasonId.markAsTouched()
            return EMPTY
        }
        const options = { ...paymentOptions, orderRefundLineIds: [...this.selectedStatusOrderLineIds] }
        const update = this.bookingService.cancelBooking(
            this.business.id,
            this.venue.id,
            area.id,
            this.booking.id,
            user,
            options,
            this.selectedMerchantCancellationReason()
        )
        return this.withProductRefundWarning(update, options)
    }

    private noShowBookingFromModal(area: Area, paymentOptions: CancelBookingPaymentOptions): Observable<Booking> {
        const options = { ...paymentOptions, orderRefundLineIds: [...this.selectedStatusOrderLineIds] }
        return this.withProductRefundWarning(this.bookingService.noShowBooking(this.business.id, this.venue.id,
            area.id, this.booking.id, options), options)
    }

    private withProductRefundWarning(update: Observable<Booking>, options: CancelBookingPaymentOptions): Observable<Booking> {
        if (options.mechanism !== 'cancellationCharge' || !options.orderRefundLineIds?.length) {
            return update
        }
        const lineIds = options.orderRefundLineIds
        return update.pipe(tap(booking => {
            if (lineIds.some(id => !booking.sale?.isLineRefunded(id))) {
                this.toastService.show(new ToastBuilder('Booking status updated, but the product refund could not be confirmed. Refresh the booking and check Payments before refunding again.')
                    .withType(ToastType.Warning).withHeader('Product refund').build())
            }
        }))
    }

    get statusOrderRefundTotal(): number {
        return this.booking.sale?.selectedRefundAmount(this.selectedStatusOrderLineIds) ?? 0
    }

    get statusOrderRefundBusy(): boolean {
        return this.statusRefundModal?.component.isPerformingAction ?? false
    }

    toggleStatusOrderLine(lineId: string) {
        if (this.statusOrderRefundBusy || !this.booking.sale?.canRefundLine(lineId)) {
            return
        }
        if (this.selectedStatusOrderLineIds.has(lineId)) {
            this.selectedStatusOrderLineIds.delete(lineId)
        } else {
            this.selectedStatusOrderLineIds.add(lineId)
        }
        this.updateStatusRefundLabels()
    }

    private configureStatusProductRefunds(component: ModalComponent, template: TemplateRef<any>, refundDeposit = false) {
        this.statusRefundTime = new Date()
        this.selectedStatusOrderLineIds = new Set(this.booking.sale?.customerRefundableLineIds(this.statusRefundTime) ?? [])
        this.showStatusProductRefundItems = false
        this.statusRefundModal = { component, actionTitle: component.actionButtonTitle ?? '', cancelTitle: component.cancelButtonTitle ?? '', refundDeposit }
        this.statusActionTemplate = template
        component.contentTemplate = this.booking.sale?.purpose === SalePurpose.BookingOrder ? this.statusActionModalBody : template
        this.updateStatusRefundLabels()
    }

    private updateStatusRefundLabels() {
        const modal = this.statusRefundModal
        if (!modal) {
            return
        }
        const total = this.statusOrderRefundTotal
        const format = (amount: number) => new Intl.NumberFormat('en-GB', { style: 'currency',
            currency: this.booking.sale?.order.currencyCode ?? 'GBP' }).format(amount / 100)
        const depositRefund = modal.refundDeposit ? this.booking.deposit?.amountRefundable() ?? 0 : 0
        modal.component.actionButtonTitle = total > 0 ? `${modal.actionTitle} · refund ${format(total + depositRefund)}` : modal.actionTitle
        modal.component.cancelButtonTitle = total > 0 && modal.component.cancelPerformsAction
            ? `${modal.cancelTitle.replace('Without Refund', 'Without Deposit Refund')} · refund ${format(total)}` : modal.cancelTitle
    }

    detailsTabSelected() {
        this.page = 'details'
    }

    customerTabSelected() {
        this.page = 'customer'
    }

    previousVisitsTabSelected() {
        if (!this.booking.customer) {
            return
        }
        this.page = 'previous-visits'
    }

    customerFeedbackTabSelected() {
        if (!this.booking.customer) {
            return
        }
        this.page = 'customer-feedback'
    }

    messagesTabSelected() {
        this.page = 'messages'
        this.loadBookingMessages()
    }

    paymentsTabSelected() {
        this.page = 'payments'
    }

    historyTabSelected() {
        this.page = 'history'
    }

    orderTabSelected() {
        this.page = 'order'
    }

    previousVisitSelected(booking: Booking) {
        if (booking.id === this.booking.id) {
            return
        }
        this.isEditingBooking = true
        forkJoin({
            otherBookings: this.bookingService.getBookings(
                this.business.id,
                this.venue.id,
                Helper.startOfDay(booking.start),
                Helper.endOfDay(booking.end),
                activeBookingStatusTypes()
            ),
            schedule: this.scheduleService.getVenueSchedule(
                this.organisation,
                this.business,
                this.venue,
                Helper.startOfDay(booking.start),
                Helper.endOfDay(booking.end)
            ),
        })
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.isEditingBooking = false)
            )
            .subscribe({
                next: ({ otherBookings, schedule }) => {
                    this.booking = booking
                    this.otherBookings = otherBookings.filter(otherBooking => otherBooking.id !== booking.id)
                    this.schedule = schedule
                    this.area = this.venue.areaBookingIsIn(this.booking) || undefined
                    this.viewModel = new BookingViewModel(
                        this.booking,
                        this.venue,
                        this
                    )
                    this.messageThread = null
                    this.messages = []
                    this.detailsTabSelected()
                },
                error: _ => this.showError('Couldn\'t load this booking. Please try again.', 'Booking Error'),
            })
    }

    sendBookingMessage(messageBody: string, subject?: string) {
        const body = messageBody.trim()
        if (!body || this.isSendingMessage || !this.venue.communicationPreferences.messagesEnabled) {
            return
        }
        this.isSendingMessage = true
        this.bookingCommunicationService.sendMessage(
            this.organisation.id,
            this.business.id,
            this.venue.id,
            this.booking.id,
            body,
            subject?.trim()
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.isSendingMessage = false)
            )
            .subscribe({
                next: message => {
                    this.messages = [...this.messages, message]
                    this.messageThread = this.threadFromBooking(this.messages)
                },
                error: _ => {
                    this.showError(
                        'Couldn\'t send this message. Please try again.',
                        'Message Error'
                    )
                },
            })
    }

    private loadBookingMessages() {
        this.messages = []
        this.messageThread = this.threadFromBooking()
        this.isLoadingMessages = true
        this.bookingCommunicationService.markMessagesReadForBooking(
            this.organisation.id,
            this.business.id,
            this.venue.id,
            this.booking.id,
        )
            .pipe(
                catchError(() => of(undefined)),
                mergeMap(() => this.bookingCommunicationService.getMessagesForBooking(
                    this.organisation.id,
                    this.business.id,
                    this.venue.id,
                    this.booking.id,
                )),
                catchError(() => of([] as BookingCommunicationMessage[])),
                takeUntil(this.onDestroy),
                finalize(() => this.isLoadingMessages = false)
            )
            .subscribe(messages => {
                this.messages = messages
                this.messageThread = this.threadFromBooking(messages)
                this.messagesUnreadCountPublisher.refreshNow()
            })
    }

    private threadFromBooking(messages = this.messages): MessageThread {
        const lastMessage = messages[messages.length - 1]
        return new MessageThread(
            this.booking.id,
            this.booking.name,
            this.booking.emailAddress,
            this.booking.start,
            this.booking.size,
            lastMessage?.messageBody ?? 'No messages yet',
            lastMessage?.senderType ?? 'CUSTOMER',
            lastMessage?.dateCreated ?? new Date(),
            messages.length,
            0,
            null,
        )
    }

    addOrder() {
        const canvasRef = this.offcanvasService.open(BookingOrderEditComponent, {
            position: 'end',
        })
        const component = canvasRef.componentInstance as BookingOrderEditComponent
        component.organisation = this.organisation
        component.business = this.business
        component.venue = this.venue
        component.booking = this.booking
        canvasRef.closed.subscribe({
            next: (order: Order) => this.saveOrder(order),
        })
    }

    editOrder() {
        const canvasRef = this.offcanvasService.open(BookingOrderEditComponent, {
            position: 'end',
        })
        const component = canvasRef.componentInstance as BookingOrderEditComponent
        component.organisation = this.organisation
        component.business = this.business
        component.venue = this.venue
        component.booking = this.booking
        canvasRef.closed.subscribe({
            next: (order: Order) => this.saveOrder(order),
        })
    }

    removeOrder() {
        this.bookingBeingChanged.emit(true)
        this.bookingService.deleteOrder(
            this.business.id,
            this.venue.id,
            this.booking
        )
            .pipe(
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(newBooking => {
                this.bookingChanged.emit([newBooking, { changeHasFinishedInteraction: false }])
                this.showToast(ToastType.Success, 'Order removed', 'Orders')
            })
    }

    addMenu() {
        const canvasRef = this.offcanvasService.open(BookingOrderDocketEditComponent, { position: 'end' })
        const component = canvasRef.componentInstance as BookingOrderDocketEditComponent
        component.organisation = this.organisation
        component.business = this.business
        component.venue = this.venue
        component.booking = this.booking
        canvasRef.closed.subscribe(({ menuId, minimumCoursesRequired, maximumCoursesRequired }: { menuId: string, minimumCoursesRequired: number | null, maximumCoursesRequired: number | null }) => {
            this.bookingBeingChanged.emit(true)
            this.bookingService.createBookingDocket(
                this.business.id,
                this.venue.id,
                this.booking.id,
                menuId,
                minimumCoursesRequired,
                maximumCoursesRequired
            )
                .pipe(finalize(() => this.bookingBeingChanged.emit(false)))
                .subscribe({
                    next: newBooking => {
                        this.bookingChanged.emit([newBooking, { changeHasFinishedInteraction: false }])
                        this.showToast(ToastType.Success, 'Menu attached. Copy the link to send to customer.', 'Pre-Order')
                    },
                    error: () => this.showError('Could not attach menu. Please try again.', 'Pre-Order')
                })
        })
    }

    editDocket(mode: 'choices' | 'limits') {
        const canvasRef = this.offcanvasService.open(BookingOrderDocketEditComponent, { position: 'end' })
        const component = canvasRef.componentInstance as BookingOrderDocketEditComponent
        component.organisation = this.organisation
        component.business = this.business
        component.venue = this.venue
        component.booking = this.booking
        component.editMode = mode
        canvasRef.closed.subscribe({
            next: (docket: BookingDocket) => this.saveDocket(docket),
        })
    }

    copyOrderLink(linkType?: MenuLinkType) {
        const label = linkType ? 'Menu' : 'Payment'
        let link = this.bookingService.getBookingDetailsUrl(this.booking)
        switch (linkType) {
            case MenuLinkType.PerCover:
                link = this.bookingService.getBookingDetailsIndividualMenuUrl(this.booking)
                break
            case MenuLinkType.SingleOrganiser:
                link = this.bookingService.getBookingDetailsMenuUrl(this.booking)
                break
        }
        if (!navigator?.clipboard) {
            const textarea = document.createElement('textarea')
            textarea.value = link
            document.body.appendChild(textarea)
            textarea.select()
            try { document.execCommand('copy') } catch (_) { /* no-op */ }
            document.body.removeChild(textarea)
            this.showToast(ToastType.Success, `${label} link copied`, 'Order')
            return
        }
        navigator.clipboard.writeText(link)
            .then(() => this.showToast(ToastType.Success, `${label} link copied`, 'Order'))
            .catch(() => this.showError('Could not copy link. Please copy manually.', 'Order'))
    }

    removeDocket() {
        if (!this.booking.docket) {
            return
        }
        this.bookingBeingChanged.emit(true)
        this.bookingService.removeBookingDocket(
            this.business.id,
            this.venue.id,
            this.booking
        )
            .pipe(finalize(() => this.bookingBeingChanged.emit(false)))
            .subscribe({
                next: newBooking => {
                    this.bookingChanged.emit([newBooking, { changeHasFinishedInteraction: false }])
                    this.showToast(ToastType.Success, 'Pre-Order removed', 'Pre-Order')
                },
                error: () => this.showError('Could not remove Pre-Order. Please try again.', 'Pre-Order')
            })
    }

    resendOrderLink(modalContent: TemplateRef<any>) {
        const payment = this.booking.canPayForOrder()
        const label = payment ? 'payment' : 'menu'
        this.orderLinkDeliveryMethod.setValue(this.booking.emailAddress ? 'email' : this.booking.phoneNumber ? 'sms' : null)
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = `Send ${label} link`
        component.contentTemplate = modalContent
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Send link'
        component.actionValidation = this.orderLinkDeliveryMethod.valueChanges.pipe(
            startWith(this.orderLinkDeliveryMethod.value),
            map(value => value !== null)
        )
        component.actionSelected.pipe(
            exhaustMap(() => {
                const method = this.orderLinkDeliveryMethod.value
                if (!method) { return EMPTY }
                component.isPerformingAction = true
                const request = payment
                    ? this.bookingService.bookingPendingPaymentResendRequested(
                        this.organisation, this.business, this.venue, this.booking, method === 'email', method === 'sms')
                    : this.bookingService.resendPreOrderMenuLink(
                        this.organisation, this.business, this.venue, this.booking, method === 'email', method === 'sms')
                return request.pipe(
                    catchError(() => {
                        this.showError(`Couldn't send the ${label} link. Please try again.`, 'Order')
                        return EMPTY
                    }),
                    finalize(() => component.isPerformingAction = false)
                )
            }),
            takeUntil(modal.hidden),
            takeUntil(this.onDestroy)
        ).subscribe(() => {
            modal.close()
            this.showToast(ToastType.Success, `${payment ? 'Payment' : 'Menu'} link queued to send`, 'Order')
        })
    }

    resendBookingConfirmationRequested(modalContent: TemplateRef<any>) {
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Resend Booking Confirmation'
        component.contentTemplate = modalContent
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Resend Confirmation'
        component.actionValidation = this.bookingConfirmationDeliveryMethod.valueChanges
            .pipe(
                map(value => value !== null)
            )
        if (this.booking.emailAddress) {
            this.bookingConfirmationDeliveryMethod.setValue('email')
        } else if (this.booking.phoneNumber) {
            this.bookingConfirmationDeliveryMethod.setValue('sms')
        }
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                tap(() => component.isPerformingAction = true),
                finalize(() => component.isPerformingAction = false),
                mergeMap(() => {
                    const sendViaEmail = this.bookingConfirmationDeliveryMethod.value === 'email'
                    const sendViaSms = this.bookingConfirmationDeliveryMethod.value === 'sms'
                    return this.bookingService.bookingConfirmationResendRequested(
                        this.organisation,
                        this.business,
                        this.venue,
                        this.booking,
                        sendViaEmail,
                        sendViaSms
                    )
                })
            )
            .subscribe({
                next: () => {
                    modal.close()
                    const deliveryMethod = this.bookingConfirmationDeliveryMethod.value === 'email' ? 'email' : 'SMS'
                    this.showToast(
                        ToastType.Success,
                        `Booking Confirmation ${deliveryMethod} sent`,
                        'Booking Confirmation Sent'
                    )
                },
                error: () => {
                    this.showError(
                        'Couldn\'t resend this booking\'s confirmation. Please try again.',
                        'Booking Confirmation Resend Error'
                    )
                },
            })
    }

    private saveOrder(order: Order) {
        this.bookingBeingChanged.emit(true)
        const request = this.booking.order?.isPaid
            ? this.bookingService.updateOrderMessage(this.organisation.id, this.business.id, this.venue.id, this.booking, order.message)
            : this.bookingService.putOrder(
                this.business.id,
                this.venue.id,
                this.booking.id,
                order
            )
        request
            .pipe(
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(newBooking => {
                this.bookingChanged.emit([newBooking, { changeHasFinishedInteraction: false }])
                this.showToast(
                    ToastType.Success,
                    this.booking.order ? 'Order updated' : 'Order added',
                    'Orders'
                )
            })
    }

    private saveDocket(docket: BookingDocket) {
        this.bookingBeingChanged.emit(true)
        this.bookingService.updateBookingDocket(
            this.business.id,
            this.venue.id,
            this.booking.id,
            docket
        )
            .pipe(finalize(() => this.bookingBeingChanged.emit(false)))
            .subscribe({
                next: newBooking => {
                    this.bookingChanged.emit([newBooking, { changeHasFinishedInteraction: false }])
                    this.showToast(ToastType.Success, 'Pre-Order updated', 'Pre-Order')
                },
                error: () => this.showError('Could not update Pre-Order. Please try again.', 'Pre-Order')
            })
    }

    acceptBooking() {
        if (!this.area) {
            return
        }
        this.bookingBeingChanged.emit(true)
        this.bookingService.acceptBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit(([booking, {}]))
            })
    }

    rejectBooking(template: TemplateRef<any>) {
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Reject Booking'
        component.actionConfiguration = {
            collectText: true,
            textLabel: 'Reason for rejection',
            textPlaceholder: '(Optional)',
            textRequired: false,
        }
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Reject'
        component.cancelButtonTitle = 'Cancel'
        component.contentTemplate = template
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                tap(() => component.isPerformingAction = true),
                finalize(() => component.isPerformingAction = false),
                mergeMap(reason => {
                    return this.rejectBookingWithReason(reason ?? null)
                })
            )
            .subscribe(booking => {
                modal.close()
                this.bookingChanged.emit([booking, {}])
            })
    }

    rejectAndRefundBooking(template: TemplateRef<any>) {
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Reject Booking'
        component.actionConfiguration = {
            collectText: true,
            textLabel: 'Reason for rejection',
            textPlaceholder: '(Optional)',
            textRequired: false,
        }
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Refund and Reject'
        component.cancelButtonTitle = 'Cancel'
        component.contentTemplate = template
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                tap(() => component.isPerformingAction = true),
                finalize(() => component.isPerformingAction = false),
                mergeMap(reason => {
                    return this.rejectBookingWithDepositRefund(reason ?? null)
                })
            )
            .subscribe(booking => {
                modal.close()
                this.bookingChanged.emit([booking, {}])
            })
    }

    private rejectBookingWithReason(reason: string | null) {
        if (!this.area) {
            throw new Error('Area is not defined for booking rejection')
        }
        this.bookingBeingChanged.emit(true)
        return this.bookingService.rejectBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            reason
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
    }

    private rejectBookingWithDepositRefund(reason: string | null) {
        if (!this.area) {
            throw new Error('Area is not defined for booking rejection')
        }
        const metadata = new Map<string, any | null>()
        if (reason) {
            metadata.set('reason', reason)
        }
        const rejectedStatus = new BookingStatus(
            uuidv4(),
            metadata,
            new Date(),
            BookingStatusType.Rejected
        )
        this.bookingBeingChanged.emit(true)
        return this.bookingService.refundDeposit(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id,
            rejectedStatus
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
    }

    makeCancelModal(
        title: string,
        actionType: ModalActionType,
        actionButtonTitle: string,
        cancelButtonTitle: string,
        cancelPerformsAction: boolean,
        modalTemplate: TemplateRef<any>,
        paymentOptions: CancelBookingPaymentOptions
    ) {
        const user = this.contextService.getUser()
        if (!user) {
            return
        }
        const area = this.area
        if (!area) {
            return
        }
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = title
        component.actionType = actionType
        component.actionButtonTitle = actionButtonTitle
        component.cancelButtonTitle = cancelButtonTitle
        component.cancelPerformsAction = cancelPerformsAction
        this.configureStatusProductRefunds(component, modalTemplate)
        this.configureMerchantCancellationReasonControl(component)
        this.chargeFailureMessage = null
        component.cancelSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.cancelBookingFromModal(user, area, paymentOptions)
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => {
                                this.chargeFailureMessage = this.getChargeFailureMessage(error)
                                return EMPTY
                            })
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.cancelBookingFromModal(user, area, {})
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => this.handleBookingUpdateError(
                                error,
                                'Couldn\'t cancel booking. Please try again.'
                            ))
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
    }

    makeCancelWithDepositModal(
        title: string,
        actionType: ModalActionType,
        actionButtonTitle: string,
        cancelButtonTitle: string,
        cancelPerformsAction: boolean,
        modalTemplate: TemplateRef<any>,
        paymentOptions: CancelBookingPaymentOptions
    ) {
        const user = this.contextService.getUser()
        if (!user) {
            return
        }
        const area = this.area
        if (!area) {
            return
        }
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = title
        component.actionType = actionType
        component.actionButtonTitle = actionButtonTitle
        component.cancelButtonTitle = cancelButtonTitle
        component.cancelPerformsAction = cancelPerformsAction
        this.configureStatusProductRefunds(component, modalTemplate, true)
        this.chargeFailureMessage = null
        this.configureMerchantCancellationReasonControl(component)
        component.cancelSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.cancelBookingFromModal(user, area, {})
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => this.handleBookingUpdateError(
                                error,
                                'Couldn\'t cancel booking. Please try again.'
                            ))
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.cancelBookingFromModal(user, area, paymentOptions)
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => this.handleBookingUpdateError(
                                error,
                                'Couldn\'t cancel booking. Please try again.'
                            ))
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
    }

    makeNoShowModal(
        title: string,
        actionType: ModalActionType,
        actionButtonTitle: string,
        cancelButtonTitle: string,
        cancelPerformsAction: boolean,
        modalTemplate: TemplateRef<any>,
        paymentOptions: Partial<{ mechanism: 'cancellationCharge' }>
    ){
        const area = this.area
        if (!area) {
            return
        }
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = title
        component.actionType = actionType
        component.actionButtonTitle = actionButtonTitle
        component.cancelButtonTitle = cancelButtonTitle
        component.cancelPerformsAction = cancelPerformsAction
        this.configureStatusProductRefunds(component, modalTemplate)
        this.chargeFailureMessage = null
        component.cancelSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.noShowBookingFromModal(area, paymentOptions)
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => {
                                this.chargeFailureMessage = this.getChargeFailureMessage(error)
                                return EMPTY
                            })
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.noShowBookingFromModal(area, {}).pipe(
                        finalize(() => component.isPerformingAction = false),
                        catchError(error => this.handleBookingUpdateError(
                            error,
                            'Couldn\'t no show booking. Please try again.'
                        ))
                    )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
    }

    makeNoShowWithDepositModal(
        title: string,
        actionType: ModalActionType,
        actionButtonTitle: string,
        cancelButtonTitle: string,
        cancelPerformsAction: boolean,
        modalTemplate: TemplateRef<any>,
        paymentOptions: Partial<{ mechanism: 'depositRefund' }>
    ){
        const area = this.area
        if (!area) {
            return
        }
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = title
        component.actionType = actionType
        component.actionButtonTitle = actionButtonTitle
        component.cancelButtonTitle = cancelButtonTitle
        component.cancelPerformsAction = cancelPerformsAction
        this.configureStatusProductRefunds(component, modalTemplate, true)
        this.chargeFailureMessage = null
        component.cancelSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.noShowBookingFromModal(area, {})
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => this.handleBookingUpdateError(
                                error,
                                'Couldn\'t no show booking. Please try again.'
                            ))
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.noShowBookingFromModal(area, paymentOptions)
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => this.handleBookingUpdateError(
                                error,
                                'Couldn\'t no show booking. Please try again.'
                            ))
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
    }

    cancelAndChargeBooking(modalTemplate: TemplateRef<any>) {
        this.makeCancelModal(
            'Cancel Booking',
            'confirm',
            'Cancel Without Charge',
            'Charge and Cancel',
            true,
            modalTemplate,
            {
                mechanism: 'cancellationCharge',
            }
        )
    }

    cancelAndRefundBookingDeposit(modalTemplate: TemplateRef<any>) {
        this.makeCancelWithDepositModal(
            'Cancel Booking',
            'confirm',
            'Refund Deposit and Cancel',
            'Cancel Without Refund',
            true,
            modalTemplate,
            {
                mechanism: 'depositRefund',
            }
        )
    }

    cancelBookingWithoutCharging(modalTemplate: TemplateRef<any>) {
        this.makeCancelModal(
            'Cancel Booking',
            'confirm',
            'Cancel Booking',
            'Cancel',
            false,
            modalTemplate,
            {}
        )
    }

    noShowBookingWithCharge(modalTemplate: TemplateRef<any>) {
        this.makeNoShowModal(
            'No Show Booking',
            'confirm',
            'No Show Without Charge',
            'Charge and No Show',
            true,
            modalTemplate,
            {
                mechanism: 'cancellationCharge',
            }
        )
    }

    NoShowBookingAndRefundDeposit(modalTemplate: TemplateRef<any>) {
        this.makeNoShowWithDepositModal(
            'No Show Booking',
            'confirm',
            'Refund Deposit and No Show',
            'No Show Without Refund',
            true,
            modalTemplate,
            {
                mechanism: 'depositRefund',
            }
        )
    }

    noShowBookingWithoutCharging(modalTemplate: TemplateRef<any>) {
        this.makeNoShowModal(
            'No Show Booking',
            'confirm',
            'No Show Booking',
            'Cancel',
            false,
            modalTemplate,
            {}
        )
    }

    voidBooking(modalTemplate: TemplateRef<any>) {
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Void Booking'
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Void Booking'
        component.cancelButtonTitle = 'Cancel'
        component.contentTemplate = modalTemplate
        component.actionType = 'delete'
        this.chargeFailureMessage = null
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy),
                filter(() => !component.isPerformingAction),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.bookingService.voidBooking(
                        this.business.id,
                        this.venue.id,
                        this.booking
                    ).pipe(
                        finalize(() => component.isPerformingAction = false),
                        catchError(error => this.handleBookingUpdateError(
                            error,
                            'Couldn\'t void booking. Please try again.'
                        ))
                    )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, {}])
                },
            })
    }

    markAsRunningLate() {
        this.bookingBeingChanged.emit(true)
        this.bookingService.markBookingAsRunningLate(
            this.organisation.id,
            this.business.id,
            this.venue.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    setPartyToWaiting() {
        if (!this.area) {
            return
        }
        this.bookingBeingChanged.emit(true)
        this.bookingService.setPartyToWaiting(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    partiallySeatBooking() {
        if (!this.area) {
            return
        }
        this.bookingBeingChanged.emit(true)
        const bringForward = this.shouldBringForwardBooking()
        if (bringForward) {
            this.seatBookingAndBringForward(
                bringForward.newStart,
                bringForward.newDuration,
                BookingStatusType.PartiallySeated
            )
            return
        }
        this.bookingService.partiallySeatBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    seatBooking() {
        if (!this.area) {
            return
        }
        this.bookingBeingChanged.emit(true)
        const bringForward = this.shouldBringForwardBooking()
        if (bringForward) {
            this.seatBookingAndBringForward(
                bringForward.newStart,
                bringForward.newDuration,
                BookingStatusType.Seated
            )
            return
        }
        this.bookingService.seatBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    unseatBooking() {
        if (!this.area) {
            return
        }
        this.bookingBeingChanged.emit(true)
        this.bookingService.unseatBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    finishBooking() {
        if (!this.area) {
            return
        }
        const now = new Date()
        now.setMinutes(now.getMinutes(), 0, 0)
        const isFinishingEarly = this.booking.isInProgressAtDate(now)
        let finishBooking: Observable<Booking>
        if (isFinishingEarly) {
            const closestBookingSlot = this.venue.bookingSlotClosestToDate(
                now,
                Helper.startOfDay(now),
                Helper.endOfDay(now),
                (startDate, endDate, interval) => {
                    return d3.timeMinute
                        .every(interval)!
                        .range(startDate, endDate)
                        .concat(endDate)
                }
            )
            if (!closestBookingSlot) {
                return
            }
            const bookingSlotEdge = this.venue.closestEdgeToEndBookingAt(now, closestBookingSlot, this.booking)
            const minutesUntilClosestBookingSlot = Helper.minutesBetweenDates(now, bookingSlotEdge)
            if (minutesUntilClosestBookingSlot === undefined) {
                return
            }
            finishBooking = this.bookingService.finishBookingEarly(
                this.business.id,
                this.venue.id,
                this.booking.id,
                minutesUntilClosestBookingSlot
            )
        } else {
            finishBooking = this.bookingService.finishBooking(
                this.business.id,
                this.venue.id,
                this.area.id,
                this.booking.id
            )
        }
        this.bookingBeingChanged.emit(true)
        finishBooking
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    reseatBooking() {
        if (!this.area) {
            return
        }
        this.bookingBeingChanged.emit(true)
        this.bookingService.reseatBooking(
            this.business.id,
            this.venue.id,
            this.area.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    updateServiceStatus(serviceStatus: ServiceStatus | null) {
        this.bookingBeingChanged.emit(true)
        this.bookingService.updateServiceStatus(
            this.business.id,
            this.venue.id,
            this.booking.id,
            serviceStatus
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe({
                next: booking => {
                    this.bookingChanged.emit([booking, {}])
                    this.showToast(
                        ToastType.Success,
                        booking.serviceStatus === null ? 'Booking returned to seated' : 'Service status updated',
                        'Booking Updated'
                    )
                },
                error: () => this.showError(
                    'Couldn\'t update this booking\'s service status. Please try again.',
                    'Booking Update Error'
                ),
            })
    }

    undoCancellation() {
        this.bookingBeingChanged.emit(true)
        const modal = this.modalService.open(ConfirmUndoCancellationComponent)
        const component = modal.componentInstance as ConfirmUndoCancellationComponent
        component.venue = this.venue
        component.booking = this.booking
        modal.closed
            .pipe(takeUntil(this.onDestroy))
            .subscribe(booking => {
                if (booking) {
                    this.bookingChanged.emit([booking, {}])
                    this.toastService.show(
                        new ToastBuilder('Cancellation was successfully undone')
                            .withType(ToastType.Success)
                            .withHeader('Undo Cancellation Successful')
                            .build()
                    )
                }
                this.bookingBeingChanged.emit(false)
            })
        modal.dismissed
            .pipe(takeUntil(this.onDestroy))
            .subscribe(() => {
                this.bookingBeingChanged.emit(false)
            })
    }

    chargeCustomer(amount: Dinero) {
        this.bookingBeingChanged.emit(true)
        this.paymentService.chargeCancellationFee(
            this.business,
            this.booking,
            amount
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(_ => {
                this.toastService.show(
                    new ToastBuilder(`Customer was charged ${amount.toFormat()}`)
                        .withType(ToastType.Success)
                        .withHeader('Charge Successful')
                        .build()
                )
            })
    }

    rebookBooking() {
        const modal = this.modalService.open(RebookBookingComponent, {
            size: 'lg',
        })
        const component = modal.componentInstance as RebookBookingComponent
        component.organisation = this.organisation
        component.business = this.business
        component.venue = this.venue
        component.sourceBooking = this.booking
        modal.closed.subscribe(() => {
            this.showToast(ToastType.Success, 'Bookings created successfully', 'Re-book')
        })
    }

    dismissClicked() {
        if (this.activeOffcanvas) {
            this.activeOffcanvas.dismiss()
            return
        }
        this.modal?.dismiss()
    }

    private showError(message: string, title?: string) {
        this.showToast(ToastType.Danger, message, title)
    }

    private getChargeFailureMessage(error: unknown): string {
        return chargeFailureMessage(error)
    }

    private handleBookingUpdateError(error: unknown, message: string): Observable<never> {
        if (isChargeFailure(error)) {
            this.chargeFailureMessage = this.getChargeFailureMessage(error)
        } else {
            this.showError(message, 'Booking Update Error')
        }
        return EMPTY
    }

    private showToast(toastType: ToastType, message: string, title?: string) {
        let  toastBuilder = new ToastBuilder(message)
            .withType(toastType)

        if (title){
            toastBuilder = toastBuilder.withHeader(title)
        }

        this.toastService.show(toastBuilder.build())
    }

    private seatBookingAndBringForward(
        newStart: Date,
        newDuration: number,
        newStatusType: BookingStatusType.PartiallySeated | BookingStatusType.Seated
    ) {
        const organisation = this.contextService.getOrganisation()
        if (!organisation) {
            return
        }
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            newStatusType
        )
        this.bookingService.changeBookingDate(
            organisation.id,
            this.business.id,
            this.venue.id,
            this.booking.id,
            newStart,
            newDuration,
            undefined,
            status
        )
            .pipe(
                takeUntil(this.onDestroy),
                finalize(() => this.bookingBeingChanged.emit(false))
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    private shouldBringForwardBooking(): { newStart: Date, newDuration: number } | false {
        const organisation = this.contextService.getOrganisation()
        if (!organisation) {
            return false
        }
        return organisation.shouldBringForwardBooking(
            this.venue,
            this.booking,
            this.otherBookings
        )
    }

    protected readonly DateTime = DateTime
    protected readonly BookingStatusType = BookingStatusType
}
